import { useEffect } from 'react';

import { formatAmount } from '~/shared/lib/formatAmount';
import { CurrencyCode, MonetaryAmount } from '~/shared/types';
import { SbpIcon } from '~/shared/ui/SbpIcon/SbpIcon';

import { CertificateCard } from '../../../../ui/components/CertificateCard/CertificateCard';
import { OfferToggle } from '../../../../ui/components/OfferToggle/OfferToggle';
import { SbpButton } from '../../../../ui/components/SbpButton/SbpButton';
import { useStorageLogin } from '../../hooks/useLastLogin';
import { useLocalization } from '../../localization/localization';
import { useSteamPageModel } from '../../model/useSteamPageModel';
import { MAX_AMOUNT, MIN_AMOUNT } from '../../utils/constants/defaultValues';
import { AmountInput } from '../AmountInput/AmountInput';
import { DiscountMark } from '../DiscountMark/DiscountMark';
import { LoginInput } from '../LoginInput/LoginInput';
import { PaymentInformationBlock } from '../PaymentInformationBlock/PaymentInformationBlock';
import { Promocode } from '../Promocode/Promocode';

import {
  BottomContent,
  ButtonLabel,
  Content,
  DividerStyled,
  Fieldset,
  inputClassName,
  Root,
  SingleCardWrapper,
  SteamCardButton,
  SteamCardImage,
  StyledAccordion,
} from './SteamPage.ui';

export const SteamPage = ({ isLoading }: { isLoading: boolean }) => {
  const { t } = useLocalization();
  const { login, saveLogin } = useStorageLogin();

  const { form, data, promocode } = useSteamPageModel();

  const {
    control,
    getErrorMessage,
    isSubmitValid,
    register,
    registerAmount,
    setValue,
  } = form;
  const { inputValue, status, isApplying, handlers } = promocode;
  const { startApply, setInputValue, reset } = handlers;

  useEffect(() => {
    if (login) {
      setValue('steamData.login', login, { shouldDirty: false });
    }
  }, [login, setValue]);

  useEffect(() => {
    return () => setValue('offer', false);
  }, [setValue]);

  const userAmount = data.amounts?.userPaymentAmount ?? null;

  const accordionData = [
    {
      title: t('Which accounts can be topped up title'),
      desc: t('Which accounts can be topped up desc'),
    },
    {
      title: t('How to find your Steam login title'),
      desc: t('How to find your Steam login desc'),
    },
  ];

  return (
    <Root>
      <Content>
        <SingleCardWrapper>
          {data.certificate && (
            <CertificateCard
              certificate={data.certificate}
              classNames={{
                button: SteamCardButton,
                image: SteamCardImage,
              }}
            />
          )}
        </SingleCardWrapper>
        <DividerStyled label={<DiscountMark />} />
        <Fieldset>
          <LoginInput
            {...register('steamData.login', {
              onChange: (e) => {
                if (e.target.value.trim()) {
                  saveLogin(e.target.value.trim());
                }
              },
            })}
            error={getErrorMessage('login')}
            inputClassName={inputClassName}
          />
          <AmountInput
            {...registerAmount}
            error={getErrorMessage('amount')}
            maxAmount={formatAmount({ amount: MAX_AMOUNT as MonetaryAmount })}
            minAmount={formatAmount({ amount: MIN_AMOUNT as MonetaryAmount })}
            inputClassName={inputClassName}
          />

          <Promocode
            inputValue={inputValue}
            status={status}
            isApplying={isApplying}
            onApply={startApply}
            onInput={setInputValue}
            onReset={reset}
            control={control}
          />

          <PaymentInformationBlock
            isPending={data.isCalculating}
            rate={(data.amounts?.rate as any) ?? null}
            steamAccountAmount={(data.amounts?.accountAmount as any) ?? null}
            userPaymentAmount={(data.amounts?.userPaymentAmount as any) ?? null}
          />
          <OfferToggle {...register('offer')} />
        </Fieldset>
        <StyledAccordion items={accordionData} />
      </Content>
      <BottomContent>
        <SbpButton
          disabled={!isSubmitValid}
          isLoading={data.isCalculating || isLoading || isApplying}
          type="submit"
        >
          <ButtonLabel>
            {t('pay button', {
              amount:
                userAmount &&
                formatAmount({
                  amount: userAmount.value as MonetaryAmount,
                  currency: userAmount.currency as CurrencyCode,
                }),
            })}
          </ButtonLabel>
        </SbpButton>
        <SbpIcon />
      </BottomContent>
    </Root>
  );
};
